import type { JSX, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
  addToast,
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from '@heroui/react';

import { useAuth } from '~/hooks/useAuth';
import {
  useOrganizationMembers,
  useResendInvitation,
  useUpdateMemberRole,
} from '~/hooks/useMembers';
import { usePermission } from '~/hooks/usePermission';
import { useTenant } from '~/hooks/useTenant';
import { getDisplayName } from '~/utils/member';

import { Feedback } from '~/components/Feedback';
import { OrganizationIcon, UsersIcon } from '~/components/Icons';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { RoleBadge } from '~/components/RoleBadge';
import { Skeleton } from '~/components/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '~/components/Table';

import type { OrganizationMember, OrgRole } from '~/types';

import { InviteMemberModal } from './InviteMemberModal';
import { RemoveMemberConfirmation } from './RemoveMemberConfirmation';
import { StatusBadge } from './StatusBadge';

interface Column {
  key: string;
  label: string;
  className?: string;
}

/**
 * Format date for display using Intl.DateTimeFormat.
 */
function formatDate(dateString: string | undefined): string {
  if (!dateString) return '-';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateString));
}

/**
 * Get disabled keys for the dropdown menu.
 */
function getDisabledKeys(member: OrganizationMember, currentUserId: string | undefined): string[] {
  const disabled: string[] = [];

  // Disable current role
  if (member.role === 'admin') {
    disabled.push('role-admin');
  }
  if (member.role === 'member') {
    disabled.push('role-member');
  }

  // Cannot change owner role
  if (member.role === 'owner') {
    disabled.push('role-admin', 'role-member');
  }

  // Owner cannot remove themselves
  if (member.user_id === currentUserId && member.role === 'owner') {
    disabled.push('remove');
  }

  return disabled;
}

export function OrganizationMembers(): JSX.Element {
  const { user } = useAuth();
  const { currentOrg, loading: tenantLoading } = useTenant();
  const { canChangeRoles, canManageMembers } = usePermission();
  const { error, isLoading, members } = useOrganizationMembers(currentOrg?.id);
  const updateRole = useUpdateMemberRole();
  const resendInvitation = useResendInvitation();

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [removeMember, setRemoveMember] = useState<OrganizationMember | null>(null);

  const currentUserId = user?.uid;

  const columns = useMemo<Column[]>(() => {
    const base: Column[] = [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role' },
      { key: 'status', label: 'Status' },
      { key: 'joined', label: 'Joined' },
    ];

    if (canManageMembers) {
      base.push({ key: 'actions', label: '', className: 'text-end w-14' });
    }

    return base;
  }, [canManageMembers]);

  const handleAction = (member: OrganizationMember, key: string): void => {
    if (!currentOrg) return;

    if (key === 'remove') {
      setRemoveMember(member);
    } else if (key === 'resend') {
      resendInvitation.mutate(
        { invitationId: member.id, orgId: currentOrg.id },
        {
          onSuccess: () => {
            addToast({
              description: `Invitation resent to ${member.email}`,
              color: 'success',
            });
          },
          onError: (mutationError: Error) => {
            addToast({
              description: mutationError.message || 'Failed to resend invitation',
              color: 'danger',
            });
          },
        },
      );
    } else if (key.startsWith('role-')) {
      const newRole = key.replace('role-', '') as OrgRole;
      updateRole.mutate(
        { orgId: currentOrg.id, userId: member.user_id, role: newRole },
        {
          onSuccess: () => {
            addToast({
              description: `${getDisplayName(member)}'s role changed to ${newRole}`,
              color: 'success',
            });
          },
          onError: (mutationError: Error) => {
            addToast({
              description: mutationError.message || 'Failed to change role',
              color: 'danger',
            });
          },
        },
      );
    }
  };

  const renderCell = (member: OrganizationMember, columnKey: string): ReactNode => {
    const isCurrentUser = member.user_id === currentUserId;

    switch (columnKey) {
      case 'name':
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{getDisplayName(member)}</span>
            {isCurrentUser && (
              <Chip color="secondary" size="sm">
                You
              </Chip>
            )}
          </div>
        );
      case 'email':
        return member.email;
      case 'role':
        return <RoleBadge role={member.role} />;
      case 'status':
        return <StatusBadge status={member.status} />;
      case 'joined':
        return formatDate(member.joined_at || member.invited_at);
      case 'actions':
        return (
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Button
                isIconOnly
                variant="light"
                size="sm"
                aria-label="Member actions"
                data-testid={`member-actions-${member.user_id}`}
              >
                <EllipsisVerticalIcon className="w-5 h-5" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Member actions"
              onAction={key => handleAction(member, key as string)}
              disabledKeys={getDisabledKeys(member, currentUserId)}
            >
              {canChangeRoles && member.status === 'active' && member.role !== 'owner' ? (
                <DropdownSection title="Change Role" showDivider>
                  <DropdownItem key="role-admin" description="Can manage members and teams">
                    Admin
                  </DropdownItem>
                  <DropdownItem key="role-member" description="Standard member access">
                    Member
                  </DropdownItem>
                </DropdownSection>
              ) : null}
              {member.status === 'invited' ? (
                <DropdownItem key="resend">Resend Invitation</DropdownItem>
              ) : null}
              <DropdownItem key="remove" color="danger">
                Remove
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        );
      default:
        return null;
    }
  };

  const renderContent = (): ReactNode => {
    if (tenantLoading) {
      return <Feedback description="Loading tenant" icon={<LoadingSpinner />} type={null} />;
    }

    if (isLoading) {
      return <Skeleton type="list" items={3} />;
    }

    if (!currentOrg) {
      return (
        <Feedback
          type="not-found"
          icon={<OrganizationIcon className="size-16" />}
          description="No organization found"
          title={null}
        />
      );
    }

    if (!canManageMembers) {
      return (
        <Feedback
          type="forbidden"
          description="You need admin or owner permissions to view organization teams."
          data-testid="members-no-permission"
        />
      );
    }

    if (error) {
      return (
        <Feedback
          description="Failed to load members. Please try again."
          icon={<UsersIcon className="size-16" />}
          type="error"
        />
      );
    }

    if (!members.length) {
      return (
        <Feedback
          description="No members to display."
          icon={<UsersIcon className="size-16" />}
          title={null}
          type="empty"
        />
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table removeWrapper className="w-full" data-testid="member-list-table">
          <TableHeader columns={columns}>
            {({ className, key, label }) => (
              <TableColumn key={key} className={className}>
                {label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody items={members}>
            {member => {
              const isCurrentUser = member.user_id === currentUserId;
              return (
                <TableRow
                  key={member.user_id}
                  data-testid={`member-row-${member.user_id}`}
                  className={isCurrentUser ? 'border-l-4 border-l-primary' : ''}
                >
                  {columns.map(column => (
                    <TableCell key={column.key}>{renderCell(member, column.key)}</TableCell>
                  ))}
                </TableRow>
              );
            }}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="Members">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Members</h2>
          <p className="text-sm">Manage who has access to your organization</p>
        </div>
        {canManageMembers && (
          <Button
            color="primary"
            startContent={<PlusIcon className="size-4" />}
            onPress={() => setInviteModalOpen(true)}
            data-testid="invite-member-button"
          >
            Invite Member
          </Button>
        )}
      </div>

      {renderContent()}

      <InviteMemberModal isOpen={inviteModalOpen} onClose={() => setInviteModalOpen(false)} />

      {removeMember && currentOrg && (
        <RemoveMemberConfirmation
          isOpen={!!removeMember}
          onClose={() => setRemoveMember(null)}
          member={removeMember}
          orgName={currentOrg.name}
        />
      )}
    </div>
  );
}
